import moment, { Moment } from "moment";
import { Task } from "../../../api/Tasks";
import { dateFormat } from "../../../misc/dates";

// "week" gives the working days, Monday to Friday, the room, and squeezes the weekend
// to a thin bar. "full-week" gives every day the same room.
export type CalendarView = "week" | "full-week" | "month";

export function parseView(value: string | null): CalendarView {
    if (value === "month" || value === "full-week") return value;
    return "week";
}

// collapsesDay says whether the view squeezes the day (0=sunday .. 6=saturday) to a
// thin bar
export function collapsesDay(day: number, view: CalendarView) {
    return view === "week" && (day === 0 || day === 6);
}

// parseAnchor reads the day the calendar is built around out of the url, falling
// back to today
export function parseAnchor(value: string | null) {
    const date = moment(value || "", dateFormat, true);
    return (date.isValid() ? date : moment()).format(dateFormat);
}

// calendarRange covers whole weeks, so a month view spills into the days of the
// months either side of it
export function calendarRange(anchor: Moment, view: CalendarView) {
    const unit = view === "month" ? "month" : "week";

    return {
        start: anchor.clone().startOf(unit).startOf("week"),
        end: anchor.clone().endOf(unit).endOf("week").startOf("day"),
    };
}

export function calendarDays(start: Moment, end: Moment) {
    const weeks: Moment[][] = [];
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, "day")) {
        const week: Moment[] = [];
        for (let i = 0; i < 7; i++) {
            week.push(cursor.clone());
            cursor.add(1, "day");
        }

        weeks.push(week);
    }

    return weeks;
}

export function rangeLabel(anchor: Moment, view: CalendarView) {
    if (view === "month") return anchor.format("MMMM YYYY");

    const start = anchor.clone().startOf("week");
    const end = start.clone().endOf("week");

    if (start.isSame(end, "month")) return start.format("MMM D") + " – " + end.format("D, YYYY");
    return start.format("MMM D") + " – " + end.format("MMM D, YYYY");
}

export type DragTask = {
    task: Task;

    // the day the drag started in; a drop on another day reschedules instead of sorting
    date: string;
};
